/**
 * no-sold-out.js
 * Precision Cut Studio – Dawn Theme Override
 *
 * PURPOSE:
 * Inventory tracking is intentionally disabled in this shop. Shopify therefore
 * always returns variant.available = true. However, Dawn's JS can still call
 * ProductForm.toggleSubmitButton(true, soldOutText) in two scenarios:
 *   1. product-info.js handleUpdateProductInfo() passes window.variantStrings.soldOut
 *      as the text argument whenever the server-rendered button has [disabled].
 *   2. product-info.js setUnavailable() calls toggleSubmitButton(true, unavailableText)
 *      when no variant is matched.
 *
 * STRATEGY (surgical, not brute-force):
 * - Intercept ONLY toggleSubmitButton calls where the text matches sold-out or
 *   unavailable strings AND the button's current primary span does NOT show a
 *   non-add-to-cart CTA ("Optionen auswählen", "Jetzt anpassen", etc.).
 * - Remove [disabled] ONLY from buttons that carry [data-sold-out-message] or
 *   whose primary span text is a sold-out/unavailable string – never from buttons
 *   that are disabled for other reasons.
 * - MutationObserver watches ONLY <product-form> and .quick-add containers,
 *   not the entire document.body.
 * - "Optionen auswählen" and any other non-add-to-cart CTA text is never touched.
 */
(function () {
  'use strict';

  // ── Resolved strings (set in init() after window.variantStrings is available) ──
  var ADD_TO_CART = 'In den Warenkorb legen';

  // Texts that indicate a sold-out / unavailable state injected by Dawn JS.
  // Populated in init() from window.variantStrings.
  var SOLD_OUT_TEXTS = [];

  // CTAs that must NEVER be replaced – these are legitimate non-add-to-cart states.
  // Dawn renders these directly in Liquid; JS does not inject them via toggleSubmitButton.
  var PROTECTED_CTAS = [
    'Optionen auswählen',
    'Choose options',
    'Jetzt anpassen',
    'Customize',
  ];

  function isSoldOutText(str) {
    var t = (str || '').trim();
    return t.length > 0 && SOLD_OUT_TEXTS.indexOf(t) !== -1;
  }

  function isProtectedCta(str) {
    var t = (str || '').trim();
    return PROTECTED_CTAS.some(function (p) { return t === p; });
  }

  /**
   * Returns the primary visible text span inside a submit button.
   * Excludes .loading__spinner, .visually-hidden, .icon-wrap, .sold-out-message.
   */
  function getPrimarySpan(btn) {
    return btn.querySelector(
      'span:not(.loading__spinner):not(.visually-hidden):not(.icon-wrap):not(.sold-out-message)'
    );
  }

  /**
   * Fix a single product-form submit button.
   *
   * Rules:
   * 1. If the button's primary span shows a PROTECTED CTA → do nothing at all.
   * 2. Remove [disabled] only if the button has [data-sold-out-message] OR its
   *    primary span text is a sold-out/unavailable string.
   * 3. Replace primary span text only if it is a sold-out/unavailable string.
   * 4. Hide .sold-out-message span only if the primary span shows add-to-cart text
   *    (i.e. we just fixed it, or it was already correct).
   */
  function fixSubmitButton(btn) {
    var primarySpan = getPrimarySpan(btn);
    var primaryText = primarySpan ? primarySpan.textContent.trim() : '';

    // Rule 1: never touch protected CTAs
    if (isProtectedCta(primaryText)) return;

    var isSoldOut = isSoldOutText(primaryText);
    var hasSoldOutAttr = btn.hasAttribute('data-sold-out-message');

    // Rule 2: remove [disabled] only when the disable reason is inventory-based
    if (btn.hasAttribute('disabled') && (isSoldOut || hasSoldOutAttr)) {
      btn.removeAttribute('disabled');
    }
    // aria-disabled is set by Dawn's onSubmitHandler during loading; only clear
    // it if the button is not in a loading state (no .loading class)
    if (btn.getAttribute('aria-disabled') === 'true' && !btn.classList.contains('loading')) {
      if (isSoldOut || hasSoldOutAttr) {
        btn.removeAttribute('aria-disabled');
      }
    }

    // Rule 3: replace sold-out text in primary span
    if (isSoldOut && primarySpan) {
      primarySpan.textContent = ADD_TO_CART;
      primarySpan.classList.remove('hidden');
    }

    // Rule 4: hide .sold-out-message span (Dawn un-hides it on cart errors)
    // Only suppress it when the primary span now shows add-to-cart text,
    // meaning the disable was inventory-based, not a real cart error.
    var soldOutMsg = btn.querySelector('.sold-out-message');
    if (soldOutMsg) {
      var currentPrimary = primarySpan ? primarySpan.textContent.trim() : '';
      if (currentPrimary === ADD_TO_CART) {
        soldOutMsg.classList.add('hidden');
      }
    }
  }

  /**
   * Scan all product-form and quick-add submit buttons.
   * Scoped to product-form and .quick-add containers only – not document-wide.
   */
  function fixProductFormButtons() {
    var containers = document.querySelectorAll('product-form, .quick-add');
    containers.forEach(function (container) {
      var btns = container.querySelectorAll(
        'button[type="submit"][name="add"], .product-form__submit, .quick-add__submit'
      );
      btns.forEach(fixSubmitButton);
    });
  }

  /**
   * Patch Dawn's ProductForm.prototype.toggleSubmitButton.
   *
   * Dawn calls this method from product-info.js in two places:
   *   A) handleUpdateProductInfo():
   *        this.productForm?.toggleSubmitButton(
   *          html.getElementById('ProductSubmitButton-...').hasAttribute('disabled') ?? true,
   *          window.variantStrings.soldOut   // <-- always passes soldOut as text
   *        );
   *      Since we removed [disabled] from the server-rendered button (Liquid patches),
   *      hasAttribute('disabled') returns false, so disable=false and this path is safe.
   *      But as a safety net we intercept the disable=true + soldOut case anyway.
   *
   *   B) setUnavailable():
   *        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
   *      This fires when no variant is matched. We intercept and do nothing (keep enabled)
   *      because inventory is not tracked and all variants are always available.
   *
   * We do NOT intercept disable=true calls with non-sold-out text (e.g. during
   * form submission loading states), preserving all other button behavior.
   */
  function patchProductFormPrototype() {
    var tryPatch = function () {
      var el = document.querySelector('product-form');
      if (!el) return false;
      var proto = Object.getPrototypeOf(el);
      if (!proto || typeof proto.toggleSubmitButton !== 'function') return false;
      if (proto.__noSoldOutPatched) return true; // already patched

      var original = proto.toggleSubmitButton;
      proto.toggleSubmitButton = function (disable, text) {
        /**
         * Intercept condition:
         * - disable === true (Dawn wants to disable the button)
         * - text matches a sold-out or unavailable string
         * - the button's primary span does NOT show a protected CTA
         *
         * In this case: call original with disable=false to keep the button
         * enabled and restore add-to-cart text.
         */
        if (disable && isSoldOutText(text)) {
          var primarySpan = getPrimarySpan(this.submitButton);
          var primaryText = primarySpan ? primarySpan.textContent.trim() : '';
          // Only intercept if the button is not showing a protected CTA
          if (!isProtectedCta(primaryText)) {
            original.call(this, false); // re-enable with add-to-cart text
            return;
          }
        }
        // All other calls pass through unchanged
        original.call(this, disable, text);
      };
      proto.__noSoldOutPatched = true;
      return true;
    };

    // Try immediately (script loads with defer, so product-form may exist)
    if (!tryPatch()) {
      // Retry after short delays in case custom element registers late
      var attempts = 0;
      var interval = setInterval(function () {
        attempts++;
        if (tryPatch() || attempts >= 10) clearInterval(interval);
      }, 200);
    }
  }

  /**
   * MutationObserver – scoped to product-form and .quick-add containers only.
   * Watches for:
   * - Attribute changes on submit buttons ([disabled], [aria-disabled])
   * - Text changes inside button spans (characterData)
   * - Child additions (e.g. quick-add modal content injected dynamically)
   */
  var observers = [];

  function observeContainer(container) {
    var obs = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (m) {
        if (m.type === 'attributes') {
          // Only care about disabled/aria-disabled on submit buttons
          return (
            (m.attributeName === 'disabled' || m.attributeName === 'aria-disabled') &&
            m.target.matches &&
            m.target.matches('button[type="submit"]')
          );
        }
        if (m.type === 'characterData') {
          // Text change inside a button span
          var parent = m.target.parentElement;
          return parent && parent.closest('button[type="submit"]') !== null;
        }
        if (m.type === 'childList') {
          // New nodes added – could be quick-add modal content
          return true;
        }
        return false;
      });
      if (relevant) fixProductFormButtons();
    });

    obs.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
    });
    observers.push(obs);
  }

  function observeAllProductForms() {
    document.querySelectorAll('product-form, .quick-add').forEach(observeContainer);
  }

  /**
   * Watch for new product-form / .quick-add elements added to the DOM
   * (e.g. quick-add modals loaded on demand).
   */
  var rootObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches('product-form, .quick-add')) {
          observeContainer(node);
          fixSubmitButton(node.querySelector('button[type="submit"]') || node);
        }
        var nested = node.querySelectorAll ? node.querySelectorAll('product-form, .quick-add') : [];
        nested.forEach(function (el) {
          observeContainer(el);
        });
      });
    });
  });

  function init() {
    // Resolve strings from window.variantStrings (set by theme.liquid inline script)
    var vs = window.variantStrings || {};
    ADD_TO_CART = vs.addToCart || 'In den Warenkorb legen';
    SOLD_OUT_TEXTS = [
      vs.soldOut || 'Ausverkauft',
      vs.unavailable || 'Nicht verfügbar',
      // English fallbacks in case locale switches
      'Sold out',
      'Unavailable',
    ].filter(function (s) { return s && s.trim().length > 0; });

    // 1. Patch the prototype before any variant-change events fire
    patchProductFormPrototype();

    // 2. Fix any buttons already in the DOM (handles server-rendered sold-out state)
    fixProductFormButtons();

    // 3. Observe existing product-form / quick-add containers
    observeAllProductForms();

    // 4. Watch for new containers added dynamically (quick-add modals, etc.)
    rootObserver.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();